/**
 * MifeLife v2 — Session Room (`/app/seans/:id`).
 *
 * Embeds the Jitsi Meet IFrame for a confirmed paid session. Logic:
 * - If user is not the session owner → 404.
 * - If session is more than 15 minutes away → show countdown + "Henüz açılmadı"
 *   prompt. (Server-time independent — uses scheduled_at vs Date.now.)
 * - During the call window → embed Jitsi iframe + sidebar info.
 * - After the call window ends → show rating modal (1-5 stars + review).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Clock, Calendar, ExternalLink, Star, CheckCircle2, Video, PhoneOff,
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { AddToCalendar, GlobalMentoringBeta } from '../../components/v2/SessionExtras';

const JOIN_BEFORE_MIN = 15;     // join button enables 15 min before

export default function V2SessionRoomPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sess, setSess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [ended, setEnded] = useState(false);   // user manually ended the call

  useEffect(() => {
    setLoading(true);
    apiRequest(`/v2/sessions/${id}`)
      .then((s) => { setSess(s); document.title = `Seans · ${s.mentor_name} · MifeLife`; })
      .catch((e) => setErr(e?.message || 'Seans bulunamadı.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  const minutesUntil = useMemo(() => {
    if (!sess?.scheduled_at) return null;
    const target = new Date(sess.scheduled_at).getTime();
    return Math.round((target - now) / 60000);
  }, [sess, now]);

  const minutesPastEnd = useMemo(() => {
    if (!sess?.scheduled_at) return null;
    const end = new Date(sess.scheduled_at).getTime() + (sess.duration_min || 60) * 60000;
    return Math.round((now - end) / 60000);
  }, [sess, now]);

  if (loading) return <Shell><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-7 h-7 text-violet-300 animate-spin" /></div></Shell>;
  if (err || !sess) return <Shell><NotFound err={err} /></Shell>;

  const canJoin = minutesUntil != null && minutesUntil <= JOIN_BEFORE_MIN && minutesPastEnd <= (sess.duration_min || 60);
  const isPast = minutesPastEnd > 0;
  const isWaiting = !canJoin && minutesUntil > 0;
  const rateable = ['paid', 'completed'].includes(sess.status);
  const isMentor = sess.viewer_role === 'mentor';
  const displayName = isMentor ? (sess.mentor_name || 'Mentör') : (sess.user_name || 'Danışan');
  // Only the mentee rates, once the session elapsed or they ended it.
  const showRating = rateable && sess.viewer_role === 'mentee' && sess.rating == null && (ended || isPast);

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
        <Link to={isMentor ? '/mentor/panel' : '/app'} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition mb-4" data-testid="v2-seans-back">
          <ArrowLeft className="w-3.5 h-3.5" /> {isMentor ? 'Panele dön' : "Dashboard'a dön"}
        </Link>

        <div className="grid lg:grid-cols-[1fr_320px] gap-5">
          {/* LEFT — video area */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden min-h-[60vh] flex flex-col" data-testid="v2-seans-stage">
            {showRating ? (
              <RatingStage sess={sess} onRated={() => apiRequest(`/v2/sessions/${id}`).then(setSess)} />
            ) : canJoin ? (
              <JitsiEmbed session={sess} onEnd={() => setEnded(true)} displayName={displayName} isMentor={isMentor} />
            ) : isWaiting ? (
              <WaitingScreen sess={sess} minutesUntil={minutesUntil} />
            ) : (
              <CompletedScreen sess={sess} isMentor={isMentor} />
            )}
          </div>

          {/* RIGHT — info + rating status */}
          <aside className="space-y-4">
            <InfoCard sess={sess} isMentor={isMentor} />
            <GlobalMentoringBeta defaultSource="tr" />
            {sess.rating != null && <RatedCard sess={sess} />}
            {isPast && <DisputeCard sess={sess} isMentor={isMentor} onChanged={() => apiRequest(`/v2/sessions/${id}`).then(setSess)} />}
          </aside>
        </div>
      </div>
    </Shell>
  );
}

// ============================================================================

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0F0F1A] text-white antialiased" data-testid="v2-seans-page">
    {children}
  </div>
);

const NotFound = ({ err }) => (
  <div className="max-w-xl mx-auto px-6 pt-32 pb-20 text-center">
    <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">404</div>
    <h1 className="text-3xl font-semibold tracking-tight mb-3">Seans bulunamadı.</h1>
    <p className="text-sm text-slate-400 mb-6">{err || 'Bu seansa erişim yetkin yok ya da seans silinmiş.'}</p>
    <Link to="/app" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 transition">Dashboard</Link>
  </div>
);

// ---------- Jitsi iframe ----------
const JitsiEmbed = ({ session, onEnd, displayName, isMentor }) => {
  // Extract Jitsi room name from the stored link (secure room token).
  const roomMatch = (session.jitsi_link || '').match(/meet\.jit\.si\/(.+)$/);
  const room = roomMatch ? roomMatch[1] : `mifelife-${(session.id || '').slice(0, 16)}`;
  const userName = encodeURIComponent(displayName || (isMentor ? 'Mentör' : 'Danışan'));
  const src = `https://meet.jit.si/${room}#userInfo.displayName="${userName}"&config.prejoinPageEnabled=false&config.disableDeepLinking=true`;
  return (
    <div className="flex flex-col flex-1 min-h-[60vh]">
      <iframe
        src={src}
        title="Mentör görüşmesi"
        allow="camera; microphone; fullscreen; display-capture; autoplay"
        className="w-full flex-1 min-h-[55vh] bg-black"
        data-testid="v2-seans-iframe"
      />
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#0a0a14] border-t border-white/[0.06]">
        <span className="hidden sm:inline text-xs text-slate-400">Görüşme bittiğinde değerlendirme ekranına geç.</span>
        <button
          onClick={onEnd}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-rose-500/90 text-white text-xs font-semibold hover:bg-rose-500 transition"
          data-testid="v2-seans-end-btn"
        >
          <PhoneOff className="w-3.5 h-3.5" /> Görüşmeyi bitir & değerlendir
        </button>
      </div>
    </div>
  );
};

// ---------- Waiting screen (countdown) ----------
const WaitingScreen = ({ sess, minutesUntil }) => {
  const formatCountdown = (mins) => {
    if (mins < 60) return `${mins} dakika`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h} saat ${m} dk`;
  };
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-10" data-testid="v2-seans-waiting">
      <div className="w-16 h-16 rounded-full bg-violet-500/15 border border-violet-400/30 flex items-center justify-center mb-5">
        <Clock className="w-7 h-7 text-violet-300" />
      </div>
      <h2 className="text-2xl font-semibold tracking-tight mb-2">Seans henüz açılmadı.</h2>
      <p className="text-sm text-slate-400 mb-1">Görüşme odası başlama saatinden <span className="text-violet-300 font-medium">15 dakika önce</span> aktif olur.</p>
      <p className="text-3xl font-semibold tabular-nums mt-6 mb-2">{formatCountdown(minutesUntil)}</p>
      <p className="text-xs text-slate-500">{new Date(sess.scheduled_at).toLocaleString('tr-TR')}</p>
    </div>
  );
};

// ---------- Completed screen ----------
const CompletedScreen = ({ sess, isMentor }) => {
  const counterpart = isMentor ? (sess.user_name || 'danışanın') : sess.mentor_name;
  return (
  <div className="flex-1 flex flex-col items-center justify-center text-center p-10" data-testid="v2-seans-completed">
    <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center mb-5">
      <CheckCircle2 className="w-7 h-7 text-emerald-300" />
    </div>
    <h2 className="text-2xl font-semibold tracking-tight mb-2">Seans tamamlandı.</h2>
    <p className="text-sm text-slate-400 mb-6">{counterpart} ile {sess.duration_min} dakikalık görüşme sona erdi.</p>
    <a href={sess.jitsi_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-violet-300 hover:text-violet-200 transition">Jitsi odasını tekrar aç <ExternalLink className="w-3.5 h-3.5" /></a>
  </div>
  );
};

// ---------- Sidebar info ----------
const InfoCard = ({ sess, isMentor }) => {
  const counterpartName = isMentor ? (sess.user_name || 'Danışan') : sess.mentor_name;
  return (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="v2-seans-info">
    <div className="flex items-center gap-3 mb-4">
      <img src={sess.mentor_photo_url} alt={counterpartName} className="w-12 h-12 rounded-xl object-cover" />
      <div className="min-w-0">
        <div className="text-sm font-semibold tracking-tight truncate">{counterpartName}</div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300">{isMentor ? 'Danışan' : 'Mentör'} · {sess.status === 'completed' ? 'Tamamlandı' : 'Onaylı'}</div>
      </div>
    </div>
    <div className="space-y-2.5 text-[13px] text-slate-300">
      <Row icon={<Calendar className="w-3.5 h-3.5 text-slate-400" />} label="Tarih" value={new Date(sess.scheduled_at).toLocaleString('tr-TR')} />
      <Row icon={<Clock className="w-3.5 h-3.5 text-slate-400" />} label="Süre" value={`${sess.duration_min} dakika`} />
      <Row icon={<Video className="w-3.5 h-3.5 text-slate-400" />} label="Görüşme" value="Jitsi Meet" />
    </div>
    <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-between gap-3">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1">{isMentor ? 'Bedel' : 'Ödenen'}</div>
        <div className="text-xl font-semibold tabular-nums">₺{sess.price_try?.toLocaleString('tr-TR') || 0}</div>
      </div>
      <AddToCalendar sessionId={sess.id} compact />
    </div>
  </div>
  );
};

const Row = ({ icon, label, value }) => (
  <div className="flex items-center gap-2">
    {icon}
    <span className="text-slate-400 text-xs uppercase tracking-[0.12em]">{label}</span>
    <span className="ml-auto text-right">{value}</span>
  </div>
);

// ---------- Rating (prominent main-stage, shown when session ends) ----------
const RatingStage = ({ sess, onRated }) => {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [review, setReview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!stars) { setErr('Lütfen önce yıldız ver.'); return; }
    setSubmitting(true); setErr(null);
    try {
      await apiRequest(`/v2/sessions/${sess.id}/rate`, {
        method: 'POST',
        body: JSON.stringify({ rating: stars, review: review.trim() || null }),
      });
      onRated && onRated();
    } catch (e) {
      setErr(e?.message || 'Kaydedilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-8 sm:p-12" data-testid="v2-seans-rating-stage">
      <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-400/30 flex items-center justify-center mb-5">
        <Star className="w-7 h-7 text-amber-300" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300 mb-2">SEANS SONA ERDİ</div>
      <h2 className="text-2xl font-semibold tracking-tight mb-1.5">{sess.mentor_name} ile görüşmeni değerlendir</h2>
      <p className="text-sm text-slate-400 mb-6 max-w-sm">Puanın diğer kullanıcılara doğru mentörü bulmakta yardımcı olur.</p>
      <div className="flex gap-1.5 mb-5" data-testid="v2-seans-stage-stars">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s} type="button"
            onClick={() => setStars(s)}
            onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)}
            className="p-1" data-testid={`v2-seans-stage-star-${s}`}
          >
            <Star className={`w-9 h-9 transition ${(hover || stars) >= s ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`} />
          </button>
        ))}
      </div>
      <textarea
        rows={3} value={review} onChange={(e) => setReview(e.target.value)}
        placeholder="Kısa yorumun (opsiyonel)…" maxLength={1000}
        className="w-full max-w-md px-3 py-2 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/50 transition resize-none mb-4"
        data-testid="v2-seans-stage-review"
      />
      {err && <div className="text-xs text-rose-300 mb-3" data-testid="v2-seans-stage-error">{err}</div>}
      <button
        onClick={submit} disabled={submitting || !stars}
        className="h-11 px-8 rounded-lg bg-amber-500 text-slate-900 text-sm font-semibold hover:bg-amber-400 disabled:opacity-50 transition flex items-center gap-2"
        data-testid="v2-seans-stage-submit"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Değerlendirmeyi gönder
      </button>
    </div>
  );
};

const RatedCard = ({ sess }) => (
  <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/[0.04] p-5" data-testid="v2-seans-rated">
    <div className="flex items-center gap-1 mb-2">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`w-4 h-4 ${(sess.rating || 0) >= s ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`} />
      ))}
    </div>
    <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-300 mb-1">DEĞERLENDİRİLDİ</div>
    {sess.review && <p className="text-[13px] text-slate-300 leading-relaxed">"{sess.review}"</p>}
  </div>
);

// ---------- Dispute (itiraz) ----------
const DISPUTE_REASONS = {
  mentee: [
    { v: 'mentor_no_show', l: 'Mentör görüşmeye katılmadı' },
    { v: 'technical_issue', l: 'Teknik bağlantı sorunu' },
    { v: 'quality', l: 'Görüşme beklentiyi karşılamadı' },
    { v: 'not_as_described', l: 'Profil/anlatım ile uyumsuz' },
    { v: 'other', l: 'Diğer' },
  ],
  mentor: [
    { v: 'mentee_no_show', l: 'Danışan görüşmeye katılmadı' },
    { v: 'technical_issue', l: 'Teknik bağlantı sorunu' },
    { v: 'other', l: 'Diğer' },
  ],
};
const DISPUTE_OUTCOME_LABEL = {
  mentee_favor_full: 'Tam iade yapıldı',
  partial: 'Kısmi iade yapıldı',
  mentor_favor: 'Mentör lehine sonuçlandı',
};

const DisputeCard = ({ sess, isMentor, onChanged }) => {
  const role = isMentor ? 'mentor' : 'mentee';
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  if (sess.dispute_status === 'open') {
    return (
      <div className="rounded-2xl border border-orange-400/25 bg-orange-500/[0.05] p-5" data-testid="v2-dispute-open">
        <div className="text-[11px] uppercase tracking-[0.18em] text-orange-300 mb-1">İtiraz incelemede</div>
        <p className="text-[13px] text-slate-300">İtirazın alındı ve inceleniyor. Sonuçlandığında bilgilendirileceksin.</p>
      </div>
    );
  }
  if (sess.dispute_status === 'resolved') {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="v2-dispute-resolved">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-1">İtiraz sonuçlandı</div>
        <p className="text-[13px] text-slate-300">{DISPUTE_OUTCOME_LABEL[sess.dispute_outcome] || 'Çözüldü'}.</p>
      </div>
    );
  }

  const submit = async () => {
    if (!reason) { setErr('Lütfen bir sebep seç.'); return; }
    setSubmitting(true); setErr(null);
    try {
      await apiRequest(`/v2/sessions/${sess.id}/dispute`, {
        method: 'POST',
        body: JSON.stringify({ reason_type: reason, message: message.trim() || null }),
      });
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.message || 'İtiraz açılamadı.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="v2-dispute-card">
      {!open ? (
        <>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1">Bir sorun mu var?</div>
          <p className="text-[13px] text-slate-400 mb-3">Görüşmeyle ilgili bir sorun yaşadıysan seans bitiminden itibaren 7 gün içinde itiraz açabilirsin.</p>
          <button onClick={() => setOpen(true)} className="w-full h-9 rounded-lg border border-white/15 text-slate-200 text-xs font-medium hover:bg-white/[0.04] transition" data-testid="v2-dispute-open-btn">
            İtiraz et
          </button>
        </>
      ) : (
        <>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">İtiraz oluştur</div>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 mb-2 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white focus:outline-none focus:border-violet-400/50" data-testid="v2-dispute-reason">
            <option value="">Sebep seç…</option>
            {DISPUTE_REASONS[role].map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Kısa açıklama (opsiyonel)…" maxLength={2000}
            className="w-full px-3 py-2 mb-2 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 resize-none" data-testid="v2-dispute-message" />
          {err && <div className="text-xs text-rose-300 mb-2" data-testid="v2-dispute-error">{err}</div>}
          <div className="flex gap-2">
            <button onClick={submit} disabled={submitting} className="flex-1 h-9 rounded-lg bg-violet-500 text-white text-xs font-semibold hover:bg-violet-400 disabled:opacity-50 transition flex items-center justify-center gap-1.5" data-testid="v2-dispute-submit">
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Gönder
            </button>
            <button onClick={() => { setOpen(false); setErr(null); }} className="h-9 px-3 rounded-lg border border-white/15 text-slate-300 text-xs hover:bg-white/[0.04] transition" data-testid="v2-dispute-cancel">İptal</button>
          </div>
        </>
      )}
    </div>
  );
};
